import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarDays, CheckCheck, Check, Timer, Wrench, X } from "lucide-react";
import { papi, errText } from "../portalApi";
import { Card, EmptyState, Field, Modal, PageHeader, Spinner, StatusBadge, fmtDate, inputCls } from "../ui";

const KIND_META = {
  leave: { label: "Leave", icon: CalendarDays },
  regularisation: { label: "Regularisation", icon: Wrench },
  timesheet: { label: "Timesheet", icon: Timer },
};

const FILTERS = [
  ["all", "All"],
  ["leave", "Leave"],
  ["regularisation", "Regularisations"],
  ["timesheet", "Timesheets"],
];

function detail(item) {
  if (item.kind === "leave") {
    return `${item.leaveTypeName} · ${fmtDate(item.fromDate)} → ${fmtDate(item.toDate)} · ${item.days} day(s)${item.halfDay ? " · half day" : ""}`;
  }
  if (item.kind === "regularisation") {
    return `${fmtDate(item.date)} · ${item.issueType}${item.session === 2 ? " · Shift 2" : ""}${item.requestedCheckIn ? ` · requested ${item.requestedCheckIn}–${item.requestedCheckOut || "?"}` : ""}`;
  }
  return `Week of ${fmtDate(item.weekStart)} · ${item.entries} entries`;
}

export default function Approvals() {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("all");
  const [action, setAction] = useState(null); // { item, type: approve|reject }
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await papi.get("/approvals");
      setItems(r.data);
    } catch (err) {
      toast.error(errText(err, "Could not load approvals."));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!items) return <Spinner />;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await papi.post(`/approvals/${action.item.kind}/${action.item.id}`, { action: action.type, comment });
      toast.success(`Request ${r.data.status.toLowerCase()}.`);
      setAction(null);
      setComment("");
      load();
    } catch (err) {
      toast.error(errText(err, "Could not update the request."));
    } finally {
      setSaving(false);
    }
  };

  const visible = items.filter((i) => filter === "all" || i.kind === filter);

  return (
    <div data-testid="approvals-page">
      <PageHeader title="Approvals" sub="Requests awaiting your decision — leave, attendance regularisations and timesheets." />

      <div className="mb-6 flex flex-wrap gap-2" data-testid="approvals-filters">
        {FILTERS.map(([key, label]) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
              filter === key ? "border-gold bg-gold/10 text-gold-dark" : "border-navy-900/15 bg-white text-slate-500 hover:border-gold/50"
            }`}
            data-testid={`approvals-filter-${key}`}
          >
            {label}
            {key !== "all" && ` (${items.filter((i) => i.kind === key).length})`}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <EmptyState icon={CheckCheck} title="All caught up" sub="There are no pending requests in this view." testid="approvals-empty" />
      ) : (
        <div className="space-y-3" data-testid="approvals-list">
          {visible.map((item) => {
            const meta = KIND_META[item.kind] || KIND_META.leave;
            const Icon = meta.icon;
            return (
              <Card key={`${item.kind}-${item.id}`} className="!p-5" testid={`approval-${item.kind}-${item.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gold/10 text-gold-dark">
                      <Icon className="h-5 w-5" aria-hidden />
                    </span>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="text-sm font-semibold text-navy-900">{item.employeeName}</p>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{meta.label}</span>
                        <StatusBadge status={item.status} />
                      </div>
                      <p className="mt-1 text-xs text-slate-600">{detail(item)}</p>
                      {item.reason && <p className="mt-1 text-xs italic text-slate-400">“{item.reason}”</p>}
                      <p className="mt-1 text-[10px] uppercase tracking-wider text-slate-400">
                        {item.department} · submitted {fmtDate(item.createdAt)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => { setAction({ item, type: "approve" }); setComment(""); }}
                      className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-emerald-700"
                      data-testid={`approve-${item.kind}-${item.id}`}
                    >
                      <Check className="h-3.5 w-3.5" aria-hidden /> Approve
                    </button>
                    <button
                      onClick={() => { setAction({ item, type: "reject" }); setComment(""); }}
                      className="inline-flex items-center gap-1.5 rounded-full border border-rose-200 bg-white px-4 py-2 text-xs font-semibold text-rose-600 transition-colors hover:bg-rose-50"
                      data-testid={`reject-${item.kind}-${item.id}`}
                    >
                      <X className="h-3.5 w-3.5" aria-hidden /> Reject
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        open={Boolean(action)}
        onClose={() => setAction(null)}
        title={action ? `${action.type === "approve" ? "Approve" : "Reject"} — ${KIND_META[action.item.kind]?.label}` : ""}
      >
        {action && (
          <form onSubmit={submit} className="space-y-4" data-testid="approval-action-form">
            <div className="rounded-xl bg-ivory px-4 py-3 text-sm text-slate-600">
              <p className="font-semibold text-navy-900">{action.item.employeeName}</p>
              <p className="mt-0.5 text-xs">{detail(action.item)}</p>
            </div>
            {action.type === "approve" && action.item.status === "Pending" && (
              <p className="rounded-lg bg-sky-50 px-3 py-2 text-xs text-sky-800">
                Manager approval moves this to the Chairman &amp; MD for final sign-off (if their approval applies).
              </p>
            )}
            <Field label={action.type === "approve" ? "Comment (optional)" : "Reason for rejection"}>
              <textarea
                rows={3}
                required={action.type === "reject"}
                className={inputCls}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={action.type === "approve" ? "Add a note…" : "Explain why this is being rejected"}
                data-testid="approval-comment"
              />
            </Field>
            <button
              type="submit"
              disabled={saving}
              className={`w-full justify-center rounded-full px-7 py-3 text-sm font-semibold transition-colors disabled:opacity-60 ${
                action.type === "approve" ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-rose-600 text-white hover:bg-rose-700"
              }`}
              data-testid="approval-confirm"
            >
              {saving ? "Saving…" : action.type === "approve" ? "Confirm Approval" : "Confirm Rejection"}
            </button>
          </form>
        )}
      </Modal>
    </div>
  );
}
